import React, { createContext, useEffect, useState } from 'react';
import { useDispatch, useLogger, useSelector } from '~Hooks';
import { Station } from '~Types';
import { AvailablePrinter, Printer } from '~reducers';

interface PrinterWithStation extends Omit<Printer, 'stations'> {
  stations: Station[];
}

interface ContextType {
  printers: PrinterWithStation[];
  availablePrinters: AvailablePrinter[];
  isScanning: boolean;
  allStations: Station[];
}

const buildTestPrintContent = (printer: PrinterWithStation) => {
  return `
    ===== TEST =====

    ${new Date().toLocaleTimeString()}

    ID: ${printer.id}

    STATIONS: ${printer.stations.map((s) => s.name).join(', ')}`;
};

const Context = createContext<ContextType>({} as ContextType);

/**
 * This component provides the printers and available printers to the rest of the app.
 * Right now this is just a wrapper around the redux store, but it could be used to
 * provide a more complex API in the future.
 */
const PrintersProvider: React.FC = ({ children }) => {
  const logger = useLogger('handy-printers');
  const printers = useSelector((state) => state.printers);
  const stations = useSelector((state) => state.stations);
  const isScanning = useSelector((state) => state.POSPrinterScanActive);
  const [hasScanned, setHasScanned] = useState(false);
  const availablePrinters = useSelector((state) => state.availablePrinters).filter(
    (p) => !printers.some((printer) => printer.id === p.id),
  );

  // -- Effects for logging
  useEffect(() => {
    if (isScanning) {
      setHasScanned(true);
    }
  }, [isScanning]);

  useEffect(() => {
    if (!hasScanned || isScanning) {
      return;
    }

    if (!availablePrinters.length) {
      logger.warn('No available printers after scan');
      return;
    }

    logger.info('Found printers during scan', {
      availablePrinters,
    });
  }, [availablePrinters.length]);
  // -- End of Effects for logging

  const printersWithStations = printers.map((printer) => {
    // Map the station ids to the actual station objects
    const printerStations = printer.stations
      .map((stationId) => stations?.find((s) => s.id === stationId))
      .filter(Boolean) as Station[];

    return {
      ...printer,
      stations: printerStations,
    };
  });

  return (
    <Context.Provider
      value={{
        printers: printersWithStations,
        allStations: stations ?? [],
        availablePrinters,
        isScanning,
      }}>
      {children}
    </Context.Provider>
  );
};

export const usePrinters = () => {
  const context = React.useContext(Context);
  const dispatch = useDispatch();
  const logger = useLogger('handy-printers');

  if (!context) {
    throw new Error('usePrinters must be used within a PrintersProvider');
  }

  // TODO: Optimistically write printer actions to the redux store straight away
  // instead of waiting for the response from the bridge
  const API = {
    add: (printer: AvailablePrinter) => {
      logger.info('Updating Settings (add printer)', {
        fields: { printer },
      });

      window.postMessageBridge({
        addPrinter: printer,
      });
    },
    remove: (printer: PrinterWithStation) => {
      logger.info('Updating Settings (remove printer)', {
        fields: { printerId: printer.id },
      });

      window.postMessageBridge({
        removePrinter: printer.id,
      });
    },
    addStation: (printer: PrinterWithStation, station: Station) => {
      logger.info('Updating Settings (add printer station)', {
        fields: { printer, newStation: station },
      });

      window.postMessageBridge({
        addStation: {
          printerId: printer.id,
          stationId: station.id,
        },
      });
    },
    setPrimary(id: string) {
      window.postMessageBridge({
        setPrimary: {
          id,
        },
      });
    },
    removeStation: (printer: PrinterWithStation, station: Station) => {
      logger.info('Updating Settings (remove printer station)', {
        fields: { printer, station },
      });

      window.postMessageBridge({
        removeStation: {
          printerId: printer.id,
          stationId: station.id,
        },
      });
    },
    startScan: () => {
      logger.info('Updating Settings (start scan)');

      window.postMessageReact('available-printers');
    },
    testPrint: (printer: PrinterWithStation) => {
      logger.info('Sending test print', {
        printer: printer.id,
      });
      window.printerManager?.initiatePrint({
        printerId: printer.id,
        data: buildTestPrintContent(printer),
        printType: 'TEST_PRINT',
      });
    },
    testPrintOnAllPrinters: () => {
      logger.info('Sending test print to all connected printers');
      dispatch({
        type: 'SEND_ORDER',
        serviceType: 'testPrinters',
      });
    },
  };

  const devDataAvailable: AvailablePrinter[] = [
    ...context.availablePrinters,
    {
      id: '192.168.0.2',
      model: 'MC_Print3',
      type: 'Type1',
    },
  ];

  const devDataAdded: PrinterWithStation[] = [
    ...context.printers,
    {
      id: '192.168.0.1',
      model: 'MC_Print3',
      coverOpen: false,
      createdAt: '',
      lastPrinted: '',
      online: true,
      paperEmpty: false,
      paperNearEmpty: false,
      primary: true,
      stations: context.allStations.length ? context.allStations.slice(0, 2) : [],
    },
  ];

  return {
    added: __DEV__ ? devDataAdded : context.printers,
    available: __DEV__ ? devDataAvailable : context.availablePrinters,
    isScanning: context.isScanning,
    allStations: context.allStations,
    ...API,
  };
};

export default PrintersProvider;
